/* eslint-disable indent */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ACTIONS, EVENTS, STATUS, Step } from "react-joyride";
import { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { Snackbar } from "@mui/material";
import { Alert } from "@mui/material";
import { captureException } from "@sentry/nextjs";
import { ChevronDown, Coins, HelpCircle, ImagePlus, Save, Upload, Wand2, X } from "lucide-react";
import Image from "next/image";
import { Loader } from "@/components/atoms/Loader";
import { TypeaheadSelect } from "@/components/atoms/TypeaheadSelect";
import { useTutorial } from "@/components/organisms/TutorialProvider";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { deletePreset, generateImage, generateNpcImage, getArtStyles, getFields, getFieldsForEntity, getModelConfig, getMonsterFields, getThemes, listPresets, onToggleVisibility, pollGenerateImage, pollGenerateNpcImage } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { useEditImage, useGenerateImage, useImageVoting, useSavePreset } from "@/lib/hooks";
import { mapCategoryToPresetType, mapPresetTypeToString } from "@/lib/utils";
import { AppReducerActionType, IArtStyle, IFieldOptions, IFreeGenModelDTO, ImageDTO, IModelConfig, IPresetDTO, IPromptOptions, Option, PresetType, RequestType, UserPremiumType } from "@/types";
import { calculateModelCost, calculateSizeCost, calculateTotalGoldCost } from "@/utils/goldCostUtils";
import { imageFormSteps } from "@/utils/joyride/imageFormSteps";
import { promptingTutorialSteps } from "@/utils/joyride/promptingTutorialSteps";
import { LOADING_TEXT } from "@/utils/promptConstants";
import { colourScheme } from "@/utils/styling/colours";
import { Span } from "@/utils/styling/typography.styled";
import CustomJoyride from "components/atoms/Joyride/index";
import ArtStyleSelection from "components/molecules/ArtStyleSelection";
import EnhancedModelControl from "components/molecules/EnhancedModelControl";
import { ImageViewer } from "components/molecules/ImageViewer";
import { LightboxWithMetadata } from "components/molecules/Lightbox";
import ModelSelection from "components/molecules/ModelSelection";
import { PresetSidebar } from "components/molecules/PresetSidebar";
import { PresetSubscriptionModal } from "components/molecules/PresetSubscriptionModal";
import QuestionModal from "components/molecules/QuestionModal";
import { SavePresetModal } from "components/molecules/SavePresetModal";
import * as Styled from "./GenerateForm.styled";
const fetchDynamicFields = async (category: string): Promise<IFieldOptions | undefined> => {
  if (category === "npcs") {
    try {
      const npcFields = await getFields();
      if (npcFields.fields) {
        delete npcFields.fields.npcFieldsMap["Art Style"];
        return npcFields.fields;
      }
    } catch (error: any) {
      captureException("Failed to fetch fields:", error);
    }
  } else if (category === "monsters") {
    try {
      const monsterFields = await getMonsterFields();
      if (monsterFields.fields) {
        const {
          monsterFieldsMap
        } = monsterFields.fields;
        delete monsterFieldsMap["Art Style"];
        return {
          npcFieldsMap: monsterFieldsMap
        } as IFieldOptions;
      }
    } catch (error: any) {
      captureException("Failed to fetch fields:", error);
    }
  } else if (category === "items") {
    try {
      const itemFields = await getFieldsForEntity("item");
      if (itemFields.fields) {
        const {
          itemFieldsMap
        } = itemFields.fields;
        delete itemFieldsMap["Art Style"];
        return {
          npcFieldsMap: itemFieldsMap
        } as IFieldOptions;
      }
    } catch (error: any) {
      captureException("Failed to fetch fields:", error);
    }
  } else {
    return undefined;
  }
};
type FormValues = {
  [key: string]: string;
};
export default function Component() {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [category, setCategory] = useState("characters");
  const [formType, setFormType] = useState("simple");
  const [dynamicFields, setDynamicFields] = useState<IFieldOptions | null>();
  const [formValues, setFormValues] = useState<FormValues>({
    "Art Style": "Realistic",
    Size: "1024x1024",
    "Number of Images": "1"
  });
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [images, setImages] = useState<ImageDTO[]>([]);
  const [error, setError] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isSavePresetModalOpen, setIsSavePresetModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [presets, setPresets] = useState<IPresetDTO[]>([]);
  const [isHowToModalOpen, setHowToModalOpen] = useState(false);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [referencePreview, setReferencePreview] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [prompt, setPrompt] = useState("");
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [useCustomConfig, setUseCustomConfig] = useState(false);
  const [useModel, setUseModel] = useState(false);
  const [config, setConfig] = useState<IFreeGenModelDTO>({
    model: "",
    steps: "",
    cfg: "",
    sampler: ""
  });
  const [modelConfigs, setModelConfigs] = useState<IModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState<IModelConfig | null>(null);
  const [artStyles, setArtStyles] = useState<IArtStyle[]>([]);
  const [themes, setThemes] = useState<IPromptOptions[]>([]);
  const [theme, setTheme] = useState<IPromptOptions | null>(themes[0]);
  const [goldCost, setGoldCost] = useState(0);
  const standardSizes = ["1024x1024", "896x1152", "832x1216", "768x1344", "640x1536", "1536x640", "1344x768", "1216x832", "1152x896"];
  const premiumSizes = ["1024x1024", "1024x1792", "1792x1024"];
  const [includeArtStyle, setIncludeArtStyle] = useState(false);
  const [showPremiumWarning, setShowPremiumWarning] = useState(false);
  const totalGoldCost = useMemo(() => {
    return calculateTotalGoldCost(selectedModel?.modelName || null, isPremium, formValues["Size"] || null, false, useCustomConfig ? parseInt(config.steps || "0", 10) : 0, isPremium ? 1 : parseInt(formValues["Number of Images"]) || 1, state.userPremiumLevel);
  }, [selectedModel, isPremium, formValues, config, useCustomConfig, state.userPremiumLevel]);
  const {
    tutorialState,
    tutorialStateInitialized,
    startTutorial,
    completeTutorial
  } = useTutorial();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (!tutorialState.generateImageForm.completed && !tutorialState.generateImageForm.started && formType === "detailed") {
      startTutorial("generateImageForm");
    }
  }, [tutorialStateInitialized, tutorialState, startTutorial, formType]);
  useEffect(() => {
    if (tutorialState?.generateImageForm.started && !tutorialState.generateImageForm.completed && formType === "detailed") {
      setRun(true);
    }
  }, [tutorialState, formType]);
  const handleJoyrideCallback = (data: {
    status: any;
    index: any;
    action: any;
    type: any;
  }) => {
    const {
      status,
      index,
      action,
      type
    } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      completeTutorial("generateImageForm");
      setRun(false);
      setStepIndex(0);
    } else if (type === "step:before") {
      if (index === 4) {
        document.getElementById("character-specific")?.click();
      }
    } else if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(prev => prev + 1);
      } else if (action === ACTIONS.PREV) {
        setStepIndex(prev => prev - 1);
      }
    }
  };
  const [runPromptTutorial, setRunPromptTutorial] = useState(false);
  const [promptStepIndex, setPromptStepIndex] = useState(0);
  useEffect(() => {
    if (!tutorialStateInitialized || !tutorialState) return;
    if (!tutorialState.promptingTutorial?.completed && !tutorialState.promptingTutorial?.started) {
      startTutorial("promptingTutorial");
    }
  }, [tutorialState, startTutorial, tutorialStateInitialized]);
  useEffect(() => {
    if (tutorialState?.promptingTutorial?.started && !tutorialState.promptingTutorial.completed && formType === "simple") {
      setRunPromptTutorial(true);
    }
  }, [formType, tutorialState]);
  const handlePromptJoyrideCallback = (data: {
    action: any;
    index: any;
    type: any;
    status: any;
  }) => {
    const {
      action,
      index,
      type,
      status
    } = data;
    const stepsToSkip = [".negativePrompt"];
    const isStepTargetAvailable = (stepIndex: number) => {
      const step = promptingTutorialSteps[stepIndex] as Step;
      return step && document.querySelector(step.target as string) !== null;
    };
    const getNextStepIndex = (currentIndex: number, direction: "next" | "prev") => {
      let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;

      // Keep checking the next/previous step and skip it if the target is unavailable
      while (stepsToSkip.includes(promptingTutorialSteps[newIndex]?.target as string) && !isStepTargetAvailable(newIndex)) {
        newIndex = direction === "next" ? newIndex + 1 : newIndex - 1;
      }
      return newIndex;
    };
    if (status === "finished" || status === "skipped" || action === ACTIONS.CLOSE) {
      setRunPromptTutorial(false);
      setPromptStepIndex(0);
      completeTutorial("promptingTutorial");
    } else if (type === "step:after") {
      // Check direction and find next or previous step index
      const nextStepIndex = getNextStepIndex(index, action === "next" ? "next" : "prev");
      setPromptStepIndex(nextStepIndex);
    }
  };
  useEffect(() => {
    if (referenceImage && isPremium) {
      setShowPremiumWarning(true);
      setReferenceImage(null);
      setReferencePreview(null);
    }
  }, [referenceImage, isPremium]);
  const handleCloseSnackbar = () => {
    setShowPremiumWarning(false);
  };
  useEffect(() => {
    const totalCost = calculateTotalGoldCost(selectedModel?.modelName || null, isPremium, formValues["Size"] || null, false, useCustomConfig ? parseInt(config.steps || "0", 10) : 0, isPremium ? 1 : parseInt(formValues["Number of Images"]) || 1, state.userPremiumLevel);
    setGoldCost(totalCost);
  }, [selectedModel, isPremium, formValues, config, prompt, useCustomConfig, state.userPremiumLevel]);
  useEffect(() => {
    const fetchArtStyles = async () => {
      try {
        const data = await getArtStyles();
        setArtStyles(data as unknown as IArtStyle[]);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchArtStyles();
  }, []);
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const {
          themes
        } = await getThemes();
        setThemes(themes);
        setTheme(themes[0]);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchThemes();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getModelConfig();
        setModelConfigs(data.modelConfigs);
        setUseModel(true);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, []);
  useEffect(() => {
    if (selectedModel?.modelName === "Flux.Pro" || isPremium) {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        "Number of Images": "1"
      }));
    }
  }, [selectedModel, isPremium]);
  useEffect(() => {
    fetchDynamicFields(category).then(fields => {
      if (fields) {
        setDynamicFields(fields);
      } else {
        setDynamicFields(null);
      }
    });
  }, [category]);
  const updateImage = (updatedImage: ImageDTO) => {
    setImages(prevImages => {
      return prevImages.map(image => image.id === updatedImage.id ? updatedImage : image);
    });
  };
  const handleSetImages = (response: string | ImageDTO[]) => {
    try {
      if (typeof response === "string") {
        setError("Invalid response format");
      } else if (Array.isArray(response)) {
        setImages(response);
      } else {
        setImages([response]);
      }
    } catch (error: any) {
      captureException("Error parsing response:", error);
    }
  };
  const handleGenerateImage = useGenerateImage({
    requestType: category === "characters" ? null : category === "npcs" ? RequestType.NPC : category === "monsters" ? RequestType.MONSTER : category === "items" ? RequestType.ITEM : category === "landscapes" ? RequestType.LANDSCAPE : category === "battlemaps" ? RequestType.BATTLEMAP : null,
    prompt: formType === "simple" ? prompt : `${theme?.shortName}`,
    file,
    charSheetUrl: url,
    generateImageFunction: category === "characters" ? generateImage : generateNpcImage,
    pollGenerateImageFunction: category === "characters" ? pollGenerateImage : pollGenerateNpcImage,
    imageFile: referenceImage,
    selectedModel,
    manualPrompt: formType === "simple" ? true : false,
    formValues,
    config,
    isPremium,
    useCustomConfig,
    useModel,
    goldCost,
    setError,
    setLoading,
    handleSetImages,
    includeArtStyle
  });
  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormValues(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };
  const handleArtStyleChange = (field: string, value: string) => {
    if (value) {
      setFormValues(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };
  const handleConfigChange = (newConfig: Partial<IFreeGenModelDTO>) => {
    setUseModel(true);
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig
    }));
  };
  const modelSupportsImageToImage = (model: IModelConfig | null) => {
    return !isPremium && model?.imageToImage;
  };
  const handleSelectChange = (field: string) => (option: {
    value: string;
    label: string;
  } | null) => {
    setFormValues(prev => ({
      ...prev,
      [field]: option ? option.value : ""
    }));
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    } else {
      setFile(null);
    }
  };
  const hasResponses = loading || images.length > 0;
  useEffect(() => {
    console.log("Has responses: ", hasResponses);
    if (loading) {
      console.log("Starting loading");
    } else {
      console.log("Stopping loading");
    }
  }, [loading, hasResponses]);
  const handleReferenceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setReferenceImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReferencePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please select a valid image file.");
    }
  };
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  };
  const handleIndexChange = (newIndex: number) => {
    setSelectedImage(images[newIndex]);
  };
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  useEffect(() => {
    if (state.gold === 0) {
      setIsPremium(false);
      setFormValues(prev => ({
        ...prev,
        Description: ""
      }));
      const selectedPresetModel = modelConfigs.find(model => model.modelName === "Flux.Dev");
      if (selectedPresetModel) {
        setSelectedModel(selectedPresetModel); // Update selected model
      }
    }
  }, [state.gold, modelConfigs]);
  const {
    handleVote
  } = useImageVoting(images, selectedImage, setImages, setSelectedImage);
  const {
    handleEditSubmit
  } = useEditImage(setIsLightboxOpen, handleSetImages);
  const {
    handleSave
  } = useSavePreset(presets, setPresets, setIsSavePresetModalOpen, formType === "characters" ? PresetType.CHARACTER : formType === "npcs" ? PresetType.NPC : formType === "monsters" ? PresetType.MONSTER : formType === "items" ? PresetType.ITEM : formType === "landscapes" ? PresetType.LANDSCAPE : formType === "battlemaps" ? PresetType.BATTLEMAP : PresetType.CHARACTER, {
    shortName: formType === "simple" ? prompt : theme?.shortName || ""
  }, formValues, url, file ?? undefined, isPremium, useCustomConfig, useModel, config, formType === "simple");
  const handleDeletePreset = async (presetId: string) => {
    await deletePreset(presetId);
    setPresets(prevPresets => prevPresets.filter(preset => preset.id !== presetId));
    toast.success("Preset deleted successfully!", {
      position: "bottom-center"
    });
  };
  useEffect(() => {
    const fetchPresets = async (category: string) => {
      try {
        if (state.isLoggedIn) {
          const response = await listPresets(mapCategoryToPresetType(category));
          if (Array.isArray(response)) {
            setPresets(response);
          } else {
            console.error("Error fetching presets:", response.error);
          }
        }
      } catch (error) {
        console.error("Error fetching presets:", error);
      }
    };
    fetchPresets(category);
  }, [category, state]);
  const handleToggle = (imageId: string) => {
    const currentVisibility = images.find(img => img.id === imageId)?.isPublic;
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setImages(prev => prev.map(image => image.id === imageId ? {
        ...image,
        isPublic: newVisibility
      } : image));
    });
  };
  const handlePremiumToggle = (checked: boolean) => {
    setIsPremium(checked);
    if (!checked) {
      setFormValues(prev => ({
        ...prev,
        Size: "1024x1024"
      }));
      handleInputChange("Size")({
        target: {
          value: "1024x1024"
        }
      } as React.ChangeEvent<HTMLInputElement>);
    }
    // If the toggle is being turned off and the user's premium level is NONE, reset the description
    if (!checked && state.userPremiumLevel === UserPremiumType.NONE) {
      handleInputChange("Description")({
        target: {
          value: ""
        }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };
  const toggleCustomField = (field: string) => {
    setCustomFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const findTheme = useMemo(() => {
    return (p: string) => themes.find(prompt => prompt.shortName === p);
  }, [themes]);
  const handleThemeChange = (selectedOption: SingleValue<Option>) => {
    if (selectedOption?.isDisabled) {
      return;
    }
    const selectedPrompt = findTheme(selectedOption?.value ?? "");
    if (!selectedPrompt) {
      setTheme({
        shortName: selectedOption?.value as string,
        fullName: selectedOption?.value as string,
        premium: false
      });
    } else {
      setTheme(selectedPrompt);
    }
    if (selectedPrompt?.premium && state.userPremiumLevel === UserPremiumType.NONE) {
      setIsPremium(true);
    }
  };
  const handleInputThemeChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const {
      value
    } = event.target;
    setTheme({
      shortName: value,
      fullName: value,
      premium: false
    });
  };
  const handleApplyPreset = (preset: IPresetDTO) => {
    setFormValues(preset.requestFields);
    setFormValues(prevFormValues => ({
      ...prevFormValues,
      "Art Style": preset.artStyle
    }));
    const existingTheme = findTheme(preset.prompt);
    if (!existingTheme) {
      setTheme({
        shortName: preset.prompt,
        fullName: preset.prompt,
        premium: false
      });
    } else {
      setTheme(existingTheme);
    }
    setUrl(preset.fileUrl || "");
    setFile(null);
    setIsPremium(preset.premium);
    const simpleForm = preset.simpleForm ? "simple" : "detailed";
    setFormType(simpleForm);
    console.log("Simple Form: ", simpleForm);
    setCategory(mapPresetTypeToString(preset.presetType));
    if (preset.freeGenModelDTO) {
      setConfig(preset.freeGenModelDTO || {
        model: selectedModel?.modelName || "Flux.Dev",
        steps: selectedModel?.defaultSteps.toString() || "28",
        cfg: selectedModel?.defaultCfgScale.toString() || "3.5",
        sampler: selectedModel?.defaultSampler || "DPM++ 2M Karras"
      });
    }
    const conf = preset.freeGenModelDTO ? true : false;
    setUseCustomConfig(preset.isCustomConfig);
    setUseModel(conf);
    const selectedPresetModel = modelConfigs.find(model => model.modelName === preset.freeGenModelDTO?.model);
    if (selectedPresetModel) {
      setSelectedModel(selectedPresetModel); // Update selected model
    }
    toast.success(`Preset "${preset.name}" applied successfully!`, {
      position: "bottom-center"
    });
  };
  const renderTransformableField = (field: string, options: string[]) => {
    const isTheme = field === "Theme";
    const isCustomField = customFields[field];
    return <Styled.AlignedFieldContainer key={field} data-sentry-element="unknown" data-sentry-component="renderTransformableField" data-sentry-source-file="index.tsx">
                <Styled.Container data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <div className="flex items-center space-x-2">
                        <Styled.StyledLabel htmlFor={field} className={isTheme ? "joyride-theme" : ""} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {field}
                        </Styled.StyledLabel>

                        <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                    <p>
                                        Select or enter a custom{" "}
                                        {field.toLowerCase()} for your image
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>

                    <Button type="button" size="sm" variant="ghost" onClick={() => toggleCustomField(field)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {customFields[field] ? "Select from List" : "Enter Free Text"}
                    </Button>
                </Styled.Container>
                {isCustomField ? <Input id={field} value={field === "Theme" ? theme?.fullName : formValues[field] || ""} onChange={field === "Theme" ? e => {
        handleInputThemeChange(e);
      } : handleInputChange(field)} placeholder={`Enter custom ${field.toLowerCase()}`} /> : isTheme ? <TypeaheadSelect name="prompt" id="prompt" placeholder="Type" required aria-required value={theme ? {
        value: theme.shortName,
        label: theme.fullName
      } : null} onChange={handleThemeChange} options={themes.map(t => ({
        value: t.shortName,
        label: t.fullName
      }))} isDisabled={loading} /> : <TypeaheadSelect id={field} name={field} value={formValues[field] ? {
        value: formValues[field],
        label: formValues[field]
      } : null} onChange={handleSelectChange(field)} options={options.map(option => ({
        value: option,
        label: option
      }))} placeholder={`Select ${field}`} isClearable={field !== "Art Style"} />}
            </Styled.AlignedFieldContainer>;
  };
  const renderCommonFields = () => <Styled.StyledGrid data-sentry-element="unknown" data-sentry-component="renderCommonFields" data-sentry-source-file="index.tsx">
            {!isPremium && <>
                    <Styled.FieldContainer className="joyride-model-selection">
                        <ModelSelection modelConfigs={modelConfigs} onConfigChange={handleConfigChange} selectedModel={selectedModel} setSelectedModel={setSelectedModel} />
                        <span className="text-[#F0B853] text-xs">
                            Cost:{" "}
                            {calculateModelCost(selectedModel?.modelName, state.userPremiumLevel)}{" "}
                            gold
                        </span>
                    </Styled.FieldContainer>
                    <Styled.FieldContainer>
                        <EnhancedModelControl config={config} onConfigChange={handleConfigChange} useCustomConfig={useCustomConfig} setUseCustomConfig={setUseCustomConfig} />
                    </Styled.FieldContainer>
                </>}
            <Styled.FieldContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Container data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.LabelTooltipContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Label htmlFor="imageSize" className="joyride-image-size" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Image Size
                        </Label>
                        <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                    <p>
                                        Select the dimensions of your generated
                                        image
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </Styled.LabelTooltipContainer>
                    {isPremium && formValues["Size"] !== "1024x1024" && <span className="text-[#F0B853] text-xs">
                            Cost: x
                            {calculateSizeCost(formValues["Size"], isPremium)}
                        </span>}
                </Styled.Container>
                <Styled.StyledSelect value={formValues["Size"]} onValueChange={value => handleSelectChange("Size")({
        value,
        label: value
      })} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <SelectTrigger className="select-trigger" data-sentry-element="SelectTrigger" data-sentry-source-file="index.tsx">
                        <SelectValue placeholder="Select Image Size" className="joyride-image-size" data-sentry-element="SelectValue" data-sentry-source-file="index.tsx" />
                    </SelectTrigger>
                    <SelectContent className="select-content" data-sentry-element="SelectContent" data-sentry-source-file="index.tsx">
                        {isPremium ? premiumSizes.map(size => <SelectItem key={size} value={size}>
                                      {size}
                                  </SelectItem>) : standardSizes.map(size => <SelectItem key={size} value={size}>
                                      {size}
                                  </SelectItem>)}
                    </SelectContent>
                </Styled.StyledSelect>
            </Styled.FieldContainer>

            <Styled.FieldContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Container data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.LabelTooltipContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Label htmlFor="numberOfImages" className="joyride-number-of-images" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Number of Images
                        </Label>
                        <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                            <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                                <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="index.tsx">
                                    <HelpCircle className="h-4 w-4 text-[#7AA8E3] cursor-help" data-sentry-element="HelpCircle" data-sentry-source-file="index.tsx" />
                                </TooltipTrigger>
                                <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="index.tsx">
                                    <p>Choose how many images to generate</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </Styled.LabelTooltipContainer>
                    <span className="text-[#F0B853] text-xs">
                        Cost: x{formValues["Number of Images"] || 1}
                    </span>
                </Styled.Container>
                <Styled.StyledSelect value={formValues["Number of Images"]} onValueChange={value => handleSelectChange("Number of Images")({
        value,
        label: value
      })} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <SelectTrigger className="select-trigger" data-sentry-element="SelectTrigger" data-sentry-source-file="index.tsx">
                        <SelectValue placeholder="Number of Images" data-sentry-element="SelectValue" data-sentry-source-file="index.tsx" />
                    </SelectTrigger>
                    <SelectContent className="select-content" data-sentry-element="SelectContent" data-sentry-source-file="index.tsx">
                        <SelectItem value="1" data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">1</SelectItem>
                        <SelectItem value="2" data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">2</SelectItem>
                        <SelectItem value="3" data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">3</SelectItem>
                        <SelectItem value="4" data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">4</SelectItem>
                    </SelectContent>
                </Styled.StyledSelect>
            </Styled.FieldContainer>

            <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5rem"
    }}>
                <Switch id="premium" checked={isPremium} onCheckedChange={handlePremiumToggle} className="joyride-premium-toggle" data-sentry-element="Switch" data-sentry-source-file="index.tsx" />
                <Label htmlFor="premium" data-sentry-element="Label" data-sentry-source-file="index.tsx">Use Premium Content</Label>
            </div>
        </Styled.StyledGrid>;
  const renderReferenceUpload = () => <Accordion type="single" collapsible data-sentry-element="Accordion" data-sentry-component="renderReferenceUpload" data-sentry-source-file="index.tsx">
            <AccordionItem value="reference-upload" className="joyride-image-upload" data-sentry-element="AccordionItem" data-sentry-source-file="index.tsx">
                <Styled.StyledAccordionTrigger data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    Upload Reference Image (Optional)
                </Styled.StyledAccordionTrigger>
                <AccordionContent data-sentry-element="AccordionContent" data-sentry-source-file="index.tsx">
                    <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem"
        }}>
                        <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
                            <Styled.StyledInput id="reference-upload" type="file" accept="image/*" onChange={handleReferenceUpload} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                            <Button type="button" size="icon" onClick={() => document.getElementById("reference-upload")?.click()} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                <ImagePlus data-sentry-element="ImagePlus" data-sentry-source-file="index.tsx" />
                            </Button>
                        </div>
                        {referencePreview && <div style={{
            position: "relative",
            width: "8rem",
            height: "8rem"
          }}>
                                <Image src={referencePreview} alt="Reference" width={150} height={150} style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "0.375rem"
            }} />
                                <Button type="button" size="icon" style={{
              position: "absolute",
              top: 0,
              right: 0,
              backgroundColor: colourScheme.error
            }} onClick={() => {
              setReferenceImage(null);
              setReferencePreview(null);
            }}>
                                    <X />
                                </Button>
                            </div>}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>;
  const renderSimpleForm = () => <div style={{
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }} data-sentry-component="renderSimpleForm" data-sentry-source-file="index.tsx">
            {renderCommonFields()}

            <div>
                <Label htmlFor="prompt" data-sentry-element="Label" data-sentry-source-file="index.tsx">Prompt</Label>
                <Styled.TextArea id="simplePrompt" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={"Enter a detailed description (e.g., 'fierce warrior on a battlefield with glowing sword') or keywords (e.g., 'warrior, armor, sword, battlefield, storm clouds')."} data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
            </div>

            {modelSupportsImageToImage(selectedModel) && renderReferenceUpload()}

            <Collapsible data-sentry-element="Collapsible" data-sentry-source-file="index.tsx">
                <Styled.StyledCollapsibleTrigger data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <span>Optional Fields</span>
                    <ChevronDown data-sentry-element="ChevronDown" data-sentry-source-file="index.tsx" />
                </Styled.StyledCollapsibleTrigger>
                <CollapsibleContent style={{
        marginTop: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem"
      }} data-sentry-element="CollapsibleContent" data-sentry-source-file="index.tsx">
                    {selectedModel?.hasNegativePrompt && <div>
                            <Label htmlFor="negativePrompt" className="negativePrompt">
                                Negative Prompt (Optional)
                            </Label>
                            <Styled.StyledInput id="negativePrompt" value={formValues.negativePrompt || ""} onChange={handleInputChange("Negative Prompt")} placeholder={"Negative Prompts allow you to specify what you don't want to see in the image. Use keywords separated by commas. Eg: 'ugly, anime, cartoon, male.' Keep it simple and to the point!"} />
                        </div>}

                    <Label htmlFor="numberOfImages" data-sentry-element="Label" data-sentry-source-file="index.tsx">Art Style:</Label>
                    <ArtStyleSelection artStyles={artStyles} onStyleChange={handleArtStyleChange} isDisabled={!includeArtStyle} data-sentry-element="ArtStyleSelection" data-sentry-source-file="index.tsx" />
                    <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}>
                        <Switch id="useArtStyle" checked={includeArtStyle} onCheckedChange={checked => setIncludeArtStyle(checked)} data-sentry-element="Switch" data-sentry-source-file="index.tsx" />
                        <Label htmlFor="premium" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Use Art Style (Optional)
                        </Label>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>;
  const renderDetailedForm = () => <div style={{
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }} data-sentry-component="renderDetailedForm" data-sentry-source-file="index.tsx">
            {renderCommonFields()}
            <Styled.StyledGrid data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.FieldContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Label htmlFor="numberOfImages" className="joyride-art-style" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                        Art Style:
                    </Label>
                    <ArtStyleSelection artStyles={artStyles} onStyleChange={handleArtStyleChange} data-sentry-element="ArtStyleSelection" data-sentry-source-file="index.tsx" />
                </Styled.FieldContainer>

                {(category === "characters" || category === "npcs" || category === "monsters") && renderTransformableField("Theme", themes.map(t => t.fullName))}
            </Styled.StyledGrid>

            {modelSupportsImageToImage(selectedModel) && renderReferenceUpload()}

            <Accordion type="single" collapsible data-sentry-element="Accordion" data-sentry-source-file="index.tsx">
                {category !== "characters" && <AccordionItem value="category-specific">
                        <Styled.StyledAccordionTrigger>
                            Category-Specific Fields
                        </Styled.StyledAccordionTrigger>
                        <AccordionContent>
                            <Styled.StyledGrid>
                                {dynamicFields && Object.entries(dynamicFields?.npcFieldsMap).map(([field, options]) => renderTransformableField(field, options))}
                                {category === "landscapes" && <>
                                        <Styled.FieldContainer>
                                            <Label htmlFor="landscapeName">
                                                Landscape Name
                                            </Label>
                                            <Styled.StyledInput id="landscapeName" value={formValues["Landscape Name"] || ""} onChange={handleInputChange("Landscape Name")} placeholder="Enter Landscape Name" />
                                        </Styled.FieldContainer>
                                        <Styled.FieldContainer>
                                            <Label htmlFor="landscapePrompt">
                                                Landscape Prompt
                                            </Label>
                                            <Styled.TextArea id="landscapePrompt" value={theme?.fullName} onChange={handleInputThemeChange} placeholder="Enter Landscape Prompt" />
                                        </Styled.FieldContainer>
                                    </>}

                                {category === "battlemaps" && <>
                                        <Styled.FieldContainer>
                                            <Label htmlFor="battlemapName">
                                                Battlemap Name
                                            </Label>
                                            <Styled.StyledInput id="battlemapName" value={formValues["Battlemap Name"] || ""} onChange={handleInputChange("Battlemap Name")} placeholder="Enter Battlemap Name" />
                                        </Styled.FieldContainer>
                                        <Styled.FieldContainer>
                                            <Label htmlFor="battlemapPrompt">
                                                Battlemap Prompt
                                            </Label>
                                            <Styled.TextArea id="battlemapPrompt" value={theme?.fullName} onChange={handleInputThemeChange} placeholder="Enter prompt for your battlemap. It's a good idea to include any rooms, top down, battlemap in your prompt." />
                                        </Styled.FieldContainer>
                                    </>}

                                {category === "items" && <Styled.FieldContainer>
                                        <Label htmlFor="itemPrompt">
                                            Item Prompt
                                        </Label>
                                        <Styled.TextArea id="itemPrompt" value={theme?.fullName} onChange={handleInputThemeChange} placeholder="Enter Item Prompt" />
                                    </Styled.FieldContainer>}
                            </Styled.StyledGrid>
                        </AccordionContent>
                    </AccordionItem>}

                {category === "characters" && <AccordionItem value="character-specific">
                        <Styled.StyledAccordionTrigger id="character-specific">
                            Character Reference (Required)
                        </Styled.StyledAccordionTrigger>
                        <AccordionContent>
                            <Styled.StyledGrid>
                                <div>
                                    <div className="flex items-center space-x-2">
                                        <Styled.StyledLabel htmlFor="file-upload" className="joyride-character-sheet-file">
                                            File Upload
                                        </Styled.StyledLabel>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <HelpCircle onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setHowToModalOpen(true);
                      }} className="h-4 w-4 text-[#7AA8E3] cursor-help" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>
                                                        Learn how to get your
                                                        PDF Character Sheet from
                                                        D&D Beyond
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem"
              }}>
                                        <Styled.StyledInput id="file-upload" type="file" accept=".pdf" onChange={handleFileChange} />
                                        <Button type="button" size="icon" onClick={() => document.getElementById("file-upload")?.click()}>
                                            <Upload />
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="url-input" className="joyride-character-sheet-url">
                                        URL Input
                                    </Label>
                                    <Styled.StyledInput id="url-input" type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="Enter URL" />
                                </div>
                            </Styled.StyledGrid>
                        </AccordionContent>
                    </AccordionItem>}

                <AccordionItem value="additional-info" data-sentry-element="AccordionItem" data-sentry-source-file="index.tsx">
                    <Styled.StyledAccordionTrigger className="joyride-additional-info" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        Additional Information
                    </Styled.StyledAccordionTrigger>
                    <AccordionContent data-sentry-element="AccordionContent" data-sentry-source-file="index.tsx">
                        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
          }}>
                            <div>
                                <Label htmlFor="prompt" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                    Additional Information:
                                </Label>
                                <Styled.TextArea id="prompt" value={formValues["Description"] || ""} onChange={handleInputChange("Description")} placeholder="Enter prompt for your image..." data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                            </div>
                            <div>
                                <Label htmlFor="negativePrompt" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                    Negative Prompt (Optional)
                                </Label>
                                <Styled.StyledInput id="negativePrompt" value={formValues.negativePrompt || ""} onChange={handleInputChange("negativePrompt")} placeholder="Describe what to exclude from the image..." data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                            </div>
                        </div>
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>;
  return <>
            <Snackbar open={showPremiumWarning} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{
      vertical: "bottom",
      horizontal: "center"
    }} data-sentry-element="Snackbar" data-sentry-source-file="index.tsx">
                <Alert onClose={handleCloseSnackbar} severity="warning" sx={{
        width: "100%"
      }} data-sentry-element="Alert" data-sentry-source-file="index.tsx">
                    Premium mode does not currently support image referencing.
                </Alert>
            </Snackbar>
            <CustomJoyride steps={imageFormSteps} run={run} stepIndex={stepIndex} disableScrolling={false} callback={handleJoyrideCallback} data-sentry-element="CustomJoyride" data-sentry-source-file="index.tsx" />
            <CustomJoyride steps={promptingTutorialSteps} run={runPromptTutorial} stepIndex={promptStepIndex} callback={handlePromptJoyrideCallback} disableScrolling={false} data-sentry-element="CustomJoyride" data-sentry-source-file="index.tsx" />
            {state.isLoggedIn && <PresetSidebar presets={presets} onClick={handleApplyPreset} onDelete={handleDeletePreset} />}
            <Styled.ScreenWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.InnerContainer hasResponses={hasResponses} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.StyledCard $hasResponses={hasResponses} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.StyledCardHeader data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.StyledCardTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                CharGen: AI Image Generator
                            </Styled.StyledCardTitle>
                            <Styled.StyledCardDescription data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                Generate fantasy images for your characters,
                                NPCs, monsters, items, landscapes, and
                                battlemaps.
                            </Styled.StyledCardDescription>
                        </Styled.StyledCardHeader>
                        <Styled.StyledCardContent data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.StyledForm onSubmit={handleGenerateImage} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Tabs value={category} onValueChange={setCategory} data-sentry-element="Tabs" data-sentry-source-file="index.tsx">
                                    <Styled.StyledTabsList data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        <Styled.StyledTabsTrigger value="characters" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            Characters
                                        </Styled.StyledTabsTrigger>
                                        <Styled.StyledTabsTrigger value="npcs" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            NPCs
                                        </Styled.StyledTabsTrigger>
                                        <Styled.StyledTabsTrigger value="monsters" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            Monsters
                                        </Styled.StyledTabsTrigger>
                                        <Styled.StyledTabsTrigger value="items" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            Items
                                        </Styled.StyledTabsTrigger>
                                        <Styled.StyledTabsTrigger value="landscapes" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            Landscapes
                                        </Styled.StyledTabsTrigger>
                                        <Styled.StyledTabsTrigger value="battlemaps" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            Battlemaps
                                        </Styled.StyledTabsTrigger>
                                    </Styled.StyledTabsList>
                                </Tabs>

                                <Styled.StyledRadioGroup value={formType} onValueChange={setFormType} className="joyride-tabs" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}>
                                        <Styled.StyledRadioGroupItem value="simple" id="simple" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                                        <Label htmlFor="simple" data-sentry-element="Label" data-sentry-source-file="index.tsx">Simple</Label>
                                    </div>
                                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}>
                                        <Styled.StyledRadioGroupItem value="detailed" id="detailed" data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                                        <Label htmlFor="detailed" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                            Detailed
                                        </Label>
                                    </div>
                                </Styled.StyledRadioGroup>

                                {formType === "simple" ? renderSimpleForm() : renderDetailedForm()}
                                <Styled.StyledCardFooter data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Styled.StyledOutlineButton type="button" className="joyride-save-preset" onClick={() => setIsSavePresetModalOpen(true)} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        <Save style={{
                    marginRight: "0.5rem"
                  }} data-sentry-element="Save" data-sentry-source-file="index.tsx" />
                                        Save Preset
                                    </Styled.StyledOutlineButton>
                                    <Styled.FlexContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        <Styled.IconTextContainer className="joyride-total-cost" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            <Coins className="h-5 w-5 text-[#F0B853]" data-sentry-element="Coins" data-sentry-source-file="index.tsx" />
                                            <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">
                                                Total Gold Cost: {totalGoldCost}
                                            </Span>
                                        </Styled.IconTextContainer>
                                        <Styled.StyledSubmitButton type="submit" isPremium={isPremium} className="joyride-generate-button" disabled={loading} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                            <Wand2 className="mr-2 h-4 w-4" data-sentry-element="Wand2" data-sentry-source-file="index.tsx" />
                                            Generate Image
                                        </Styled.StyledSubmitButton>
                                    </Styled.FlexContainer>
                                </Styled.StyledCardFooter>
                            </Styled.StyledForm>
                        </Styled.StyledCardContent>
                    </Styled.StyledCard>
                    {error && <p>{error}</p>}
                    {hasResponses && <Styled.ImageViewerContainer $hasImages={hasResponses}>
                            <div className="content">
                                {loading ? <Loader prompts={LOADING_TEXT} /> : <ImageViewer imageDtos={images} onImageClick={handleImageClick} onVote={handleVote} updateImage={updateImage} onSubmitEdit={handleEditSubmit} />}
                            </div>
                        </Styled.ImageViewerContainer>}
                </Styled.InnerContainer>
            </Styled.ScreenWrapper>
            {selectedImage && <LightboxWithMetadata imageDtos={images} initialImageId={selectedImage.id} onClose={handleLightboxClose} open={isLightboxOpen} onToggleVisibility={handleToggle} onVote={handleVote} onIndexChange={handleIndexChange} />}
            <QuestionModal isOpen={isHowToModalOpen} handleBackdropClick={() => setHowToModalOpen(false)} data-sentry-element="QuestionModal" data-sentry-source-file="index.tsx" />
            <PresetSubscriptionModal isOpen={isSubscriptionModalOpen} onCancel={() => setIsSubscriptionModalOpen(false)} data-sentry-element="PresetSubscriptionModal" data-sentry-source-file="index.tsx" />
            <SavePresetModal isOpen={isSavePresetModalOpen} onCancel={() => setIsSavePresetModalOpen(false)} presets={presets} onSave={handleSave} data-sentry-element="SavePresetModal" data-sentry-source-file="index.tsx" />
        </>;
}